import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import { DetaljerTab } from "@/components/project/DetaljerTab";
import { ReportStyringSection } from "@/components/project/report/ReportStyringSection";
import { ReportDebriefSection } from "@/components/project/report/ReportDebriefSection";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { useProjectReport } from "@/hooks/use-project-report";
import { computeBudgetLineLoggedHours } from "@/hooks/use-budget-line-logged-hours";
import {
  accruedExternalByLine,
  calcBillingContributionHours,
  calcDebrief,
  calcExternalCosts,
  calcInvoiced,
  calcPersonHours,
  calcPhaseHours,
} from "@/lib/project-report-calc";
import { formatNOK } from "@/lib/format";
import { InfoRow } from "@/components/project/BudgetTab";

/**
 * Rapport-seksjonen under Økonomi.
 * Viser prosjektdetaljer pluss økonomiske nøkkeltall:
 * - Kontraktssum, internbeløp
 * - Budsjetterte/loggede timer
 * - Timepris estimert/reell
 * - Kostnader fra godkjente bilag
 *
 * Timepris er basert på internt budsjett (rolle-linjer).
 */
export default function RapportPage() {
  const {
    project,
    customer,
    allUsers,
    userMap,
    budgetLines,
    timeEntries,
    vouchers,
    voucherAllocations,
    invoices,
    permissions,
  } = useProjectDetailContext();

  // VIB-1068: full styrings- + debrief-rapport.
  const { data: baseline = [], isLoading: baselineLoading } = useProjectReport(project.id);

  // VIB-722c: view_economics gates hele økonomi-rapporten. Admin/superadmin
  // overstyrer. Brukere uten tilgang sendes tilbake til prosjektoversikten.
  if (!permissions.view_economics) {
    return <Navigate to={`/prosjekter/${project.id}`} replace />;
  }

  // Internbeløp og interne budsjetterte timer (kun rolle-linjer / hourly)
  const internalLines = budgetLines.filter((l) => l.cost_type === "internal");
  const internalAmount = internalLines.reduce((s, l) => s + (Number(l.amount) ?? 0), 0);
  const budgetedHours = internalLines.reduce(
    (s, l) => s + (Number(l.quantity_hours) ?? 0),
    0,
  );

  const totalTimeSec = timeEntries.reduce((s, t) => s + (t.duration_seconds ?? 0), 0);
  const loggedHours = totalTimeSec / 3600;

  const estHourlyRate =
    budgetedHours > 0 ? Math.round(internalAmount / budgetedHours) : null;
  const actualHourlyRate =
    loggedHours > 0 ? Math.round(internalAmount / loggedHours) : null;

  // Godkjente bilag
  const approvedVouchers = vouchers.filter((v: any) => v.status === "approved");
  const voucherCosts = approvedVouchers.reduce(
    (s: number, v: any) => s + (Number(v.amount) ?? 0),
    0,
  );

  // VIB-1068: avledede rapport-tall (rene funksjoner, READ-ONLY)
  const logged = computeBudgetLineLoggedHours(timeEntries, budgetLines);
  const phaseHours = calcPhaseHours(budgetLines, logged.totals);
  const personHours = calcPersonHours(budgetLines, logged.perUser);
  const externalCosts = calcExternalCosts(budgetLines, accruedExternalByLine(voucherAllocations));
  const invoiced = calcInvoiced(invoices, project.total_amount);
  const billingContributionHours = calcBillingContributionHours(budgetLines);
  const debrief = calcDebrief(budgetLines, baseline);

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <ReportStyringSection
          phaseHours={phaseHours}
          personHours={personHours}
          externalCosts={externalCosts}
          invoiced={invoiced}
          bfEstimatedKr={Number(project.bf_estimated ?? 0)}
          bfActualKr={Number(project.bf_actual ?? 0)}
          billingContributionHours={billingContributionHours}
          userMap={userMap}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Økonomi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <InfoRow
                label="Kontraktssum"
                value={project.total_amount != null ? formatNOK(project.total_amount) : null}
              />
              <InfoRow label="Internbeløp" value={formatNOK(internalAmount)} />
              <InfoRow label="Kostnader (bilag)" value={formatNOK(voucherCosts)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timer og timepris</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <InfoRow
                label="Budsjetterte timer"
                value={`${budgetedHours.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} t`}
              />
              <InfoRow
                label="Loggede timer"
                value={`${loggedHours.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} t`}
              />
              <InfoRow
                label="Timepris estimert"
                value={estHourlyRate != null ? `${formatNOK(estHourlyRate)}/t` : null}
              />
              <InfoRow
                label="Timepris reell"
                value={actualHourlyRate != null ? `${formatNOK(actualHourlyRate)}/t` : null}
              />
            </CardContent>
          </Card>
        </div>

        <ReportDebriefSection debrief={debrief} project={project} isLoading={baselineLoading} />

        <DetaljerTab project={project} customer={customer} allUsers={allUsers} />
      </div>
    </ErrorBoundary>
  );
}
